import { type SeasonUserDto, estimateReward } from '@penalty-cup/shared';
import { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import { ApiError, api } from '../api.js';
import { useApp } from '../store.js';
import { useBackButton } from '../telegram-back.js';

const TokenSection = lazy(() => import('../components/TokenSection.js'));

function endsIn(iso: string, now: number): string {
  const min = Math.max(0, Math.floor((Date.parse(iso) - now) / 60_000));
  const d = Math.floor(min / 1440);
  const h = Math.floor((min % 1440) / 60);
  return d > 0 ? `${d} д ${h} ч` : `${h} ч ${min % 60} мин`;
}

/** Сезон Gold (Economy v0.1, п. 66; фаза 3 — симуляция): вклад Gold и оценка доли пула. */
export function Season() {
  const token = useApp((s) => s.token);
  const setView = useApp((s) => s.setView);
  const back = useCallback(() => setView('exchange'), [setView]);
  useBackButton(back);
  const [data, setData] = useState<SeasonUserDto | null>(null);
  const [amount, setAmount] = useState(1);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());

  const apply = useCallback((d: SeasonUserDto) => {
    setData(d);
    setNow(Date.now());
    const me = useApp.getState().me;
    if (me)
      useApp.setState({
        me: { ...me, gold: { ...me.gold, claimable: d.availableGold, committed: d.userCommittedGold } },
      });
  }, []);

  useEffect(() => {
    if (!token) return;
    let alive = true;
    api.seasonUser(token).then(
      (d) => alive && apply(d),
      () => alive && setMsg('Сезон не загрузился. Проверьте связь'),
    );
    return () => {
      alive = false;
    };
  }, [token, apply]);

  if (!data) {
    return (
      <main className="screen season screen--nav">
        <h1 className="screen__title">Сезон Gold</h1>
        <p className="screen__lead">{msg ?? 'Загружаем…'}</p>
      </main>
    );
  }
  const s = data.season;
  if (!s) {
    return (
      <main className="screen season screen--nav">
        <h1 className="screen__title">Сезон Gold</h1>
        <p className="screen__lead">Новый сезон скоро начнётся — копите Gold: обмен и кубки без ботов.</p>
        <Suspense fallback={null}>
          <TokenSection />
        </Suspense>
      </main>
    );
  }
  const n = Math.min(Math.max(1, amount), Math.max(1, data.availableGold));
  // Оценка, если вложить ещё n: доля растёт, но и общий вклад тоже
  const after = estimateReward(s.tokenPool, data.userCommittedGold + n, s.totalCommittedGold + n);

  const commit = async () => {
    if (!token || busy) return;
    setBusy(true);
    setMsg(null);
    try {
      apply(await api.commitGold(token, n));
      setMsg(`Вложено ${n} 🟡`);
      setAmount(1);
    } catch (err) {
      setMsg(
        err instanceof ApiError && err.message === 'not_enough_gold' ? 'Не хватает Gold' : 'Не получилось',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="screen season screen--nav">
      <h1 className="screen__title">Сезон Gold {s.number}</h1>
      {s.simulated && (
        <p className="season__sim" role="note">
          🧪 Симуляция: награда считается, но токен пока не выдаётся
        </p>
      )}
      <div className="profile__stats">
        <div className="stat">
          <b>{endsIn(s.endsAt, now)}</b>
          <span>до конца</span>
        </div>
        <div className="stat">
          <b>{s.tokenPool.toLocaleString('ru-RU')}</b>
          <span>пул PEN</span>
        </div>
        <div className="stat">
          <b>{data.availableGold} 🟡</b>
          <span>можно вложить</span>
        </div>
        <div className="stat">
          <b>{data.userCommittedGold} 🟡</b>
          <span>вложено</span>
        </div>
      </div>
      <p className="season__estimate">
        Оценка награды:{' '}
        <b>≈ {data.estimatedTokenReward.toLocaleString('ru-RU', { maximumFractionDigits: 4 })} PEN</b>
      </p>
      <p className="field__hint">
        Всего вложено {s.totalCommittedGold.toLocaleString('ru-RU')} 🟡 от {s.participants} игроков. Оценка
        меняется до конца сезона: пул делится пропорционально вкладу.
      </p>
      {data.availableGold > 0 && (
        <div className="season__commit">
          <div className="season__stepper">
            <button
              type="button"
              className="chip"
              onClick={() => setAmount(Math.max(1, n - 1))}
              aria-label="Меньше"
            >
              −
            </button>
            <b aria-live="polite">{n} 🟡</b>
            <button
              type="button"
              className="chip"
              onClick={() => setAmount(Math.min(data.availableGold, n + 1))}
              aria-label="Больше"
            >
              +
            </button>
            <button type="button" className="chip chip--small" onClick={() => setAmount(data.availableGold)}>
              Всё
            </button>
          </div>
          <button type="button" className="play play--small" disabled={busy} onClick={() => void commit()}>
            Вложить {n} 🟡
          </button>
          <p className="field__hint">
            После вклада оценка ≈ {after.toLocaleString('ru-RU', { maximumFractionDigits: 4 })} PEN. Вложенный
            Gold заблокирован до конца сезона и после расчёта сгорает.
          </p>
        </div>
      )}
      {msg && (
        <p className="field__hint" role="status">
          {msg}
        </p>
      )}
      {data.lastSettlement && (
        <p className="field__hint">
          Сезон {data.lastSettlement.season}: вложено {data.lastSettlement.committedGold} 🟡 →{' '}
          {data.lastSettlement.tokenAmount} PEN{data.lastSettlement.simulated ? ' (симуляция)' : ''}
        </p>
      )}
      <p className="field__hint">
        В сезон идёт только обычный Gold (обмен и кубки без ботов), бонусный — нет.
      </p>
      <Suspense fallback={null}>
        <TokenSection />
      </Suspense>
    </main>
  );
}
